import { memo, useCallback } from 'react'
import type { CalendarEvent } from '@highlight/common'
import { CalendarEventIconView } from '@renderer/components/Icons/CalendarEventIcon'
import type { Calendar } from '@renderer/hooks/use-calendars'
import { useViewActions, useViewOpenHandlers, type ViewOpenTarget } from '../../view-atoms'
import { FlatActivityRow } from '../../components/history/history-row'
import { useOpenBrief } from '../../components/meeting/brief/use-open-brief'
import { MEETING_VIEW_ID } from '../../components/meeting/meeting-view-id'

const SOURCE_LABEL = 'Earlier Today'

interface PastEventRowProps {
  event: CalendarEvent
  calendars: Array<Calendar> | undefined
}

/**
 * "Earlier Today" row for a past event nothing was recorded for: the same
 * compact `FlatActivityRow` as the recorded-note rows beside it (`HistoryRow`),
 * just sourced from a calendar event.
 *
 * Not `MeetingRowView`: that row carries the time line and ended-state dimming
 * of the upcoming list, and here the section reads as history, not schedule.
 */
export const PastEventRow = memo(function PastEventRow({ event, calendars }: PastEventRowProps) {
  const { openView } = useViewActions()
  const openBrief = useOpenBrief()

  // Same navigation as clicking the event anywhere else: land on its brief when
  // one exists, otherwise the plain meeting view.
  const openWith = useCallback(
    (open: ViewOpenTarget) => {
      if (event.brief?.conversationId || event.briefGenerating) {
        openBrief({
          calendarEventId: event.id,
          title: event.title,
          meeting: event,
          sourceLabel: SOURCE_LABEL,
          ...open,
        })
        return
      }
      openView(
        MEETING_VIEW_ID,
        { mode: 'note', calendarEventId: event.id, title: event.title, meeting: event },
        { sourceLabel: SOURCE_LABEL, ...open },
      )
    },
    [event, openBrief, openView],
  )
  const { onOpen, onPopout } = useViewOpenHandlers(openWith)

  return (
    <FlatActivityRow
      ariaLabel={event.title}
      onOpen={onOpen}
      onOpenInWindow={onPopout}
      leading={
        <div className="flex size-5 shrink-0 items-center justify-center">
          <CalendarEventIconView event={event} size={16} calendars={calendars} />
        </div>
      }
      title={event.title}
      timestamp={event.end || event.start}
    />
  )
})
